"use client";

import { forwardRef, useMemo } from "react";
import {
  Label as AriaLabel,
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
} from "react-aria-components";

import { cn } from "../../utils/cn";
import { SegmentedControlProvider, useSegmentedControlContext } from "./context";
import { segmentedControlVariants } from "./segmentedControlVariants";

import type { RadioGroupProps as AriaRadioGroupProps, RadioProps } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { SegmentedControlClassNames } from "./segmentedControlVariants";

// SegmentedControl - A segmented control allows users to make a single selection from a set of 2-5 options.
// Visually appears as a group of connected buttons, functioning as a radio button group.
//
// Structure:
// - <SegmentedControl> - Container that manages selection state
// - <SegmentedControl.Button> - Individual option button (requires unique `value`)
//
// Built on React Aria Components RadioGroup:
// https://react-spectrum.adobe.com/react-aria/RadioGroup.html

export interface SegmentedControlProps
  extends Omit<AriaRadioGroupProps, "children" | "orientation">,
    VariantProps<typeof segmentedControlVariants> {
  children?: React.ReactNode;
  /** Label text for the segmented control group */
  label?: string;
  /** Custom class names for different slots */
  classNames?: SegmentedControlClassNames;
}

export interface SegmentedControlButtonProps extends Omit<RadioProps, "className" | "children"> {
  /** Additional class name for the button */
  className?: string;
  /** Style prop for custom styling (e.g., width) */
  style?: React.CSSProperties;
  /** Button content */
  children?: React.ReactNode;
}

const SegmentedControlButton = forwardRef<HTMLLabelElement, SegmentedControlButtonProps>(
  ({ className, children, style, ...props }, ref) => {
    const { slots, classNames } = useSegmentedControlContext();

    return (
      <AriaRadio
        {...props}
        ref={ref}
        style={style}
        className={slots.button({ className: cn(classNames?.button, className) })}
      >
        {() => (
          <span className={cn(slots.buttonContent(), classNames?.buttonContent)}>{children}</span>
        )}
      </AriaRadio>
    );
  }
);
SegmentedControlButton.displayName = "SegmentedControl.Button";

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(
  (
    { className, classNames, label, children, size, fullWidth, variant = "solid", ...props },
    ref
  ) => {
    const slots = useMemo(
      () => segmentedControlVariants({ size, fullWidth, variant }),
      [size, fullWidth, variant]
    );
    const context = useMemo(() => ({ slots, classNames }), [slots, classNames]);

    return (
      <AriaRadioGroup
        {...props}
        ref={ref}
        orientation="horizontal"
        className={cn(slots.group(), classNames?.group, className)}
      >
        {label && (
          <AriaLabel className={cn(slots.groupLabel(), classNames?.groupLabel)}>{label}</AriaLabel>
        )}
        <div className={cn(slots.buttonsWrapper(), classNames?.buttonsWrapper)}>
          <SegmentedControlProvider value={context}>{children}</SegmentedControlProvider>
        </div>
      </AriaRadioGroup>
    );
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  { Button: typeof SegmentedControlButton },
  SegmentedControlProps
>;
SegmentedControl.displayName = "SegmentedControl";
SegmentedControl.Button = SegmentedControlButton;

export default SegmentedControl;
